import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { ApiConfig } from "@/config/api.config";
import { ApiHookProps } from '@/@types/api.types';
import { CreateNewAndOfferRequestDto, GetNewAndOfferDetailsResponseDto, NewsAndOffersStatus } from '@/features/news-and-offers/types/news-and-offers-dto';
import EToast from '@/components/everridge/EToast';
import { t } from 'i18next';
import { useAxiosAuth } from '@/hooks/useAxiosAuth';

type Props = {} & ApiHookProps;

export const useNewAndOfferDetailsApi = ({ id }: Props = {}) => {
    const queryClient = useQueryClient();
    const axiosInstance = useAxiosAuth();

    const {
        data: newAndOfferDetailsData, 
        isLoading: isLoadingNewAndOfferDetailsData, 
        isError: isErrorNewAndOfferDetailsData
    } = useQuery({
        queryKey: [ApiConfig.queries.features.newsAndOffers.queryKeys.details, id],
        queryFn: async (): Promise<GetNewAndOfferDetailsResponseDto> => {
            const url = `${ApiConfig.queries.features.newsAndOffers.endpoints.get}/${id}`;               
            const { data } = await axiosInstance.get<GetNewAndOfferDetailsResponseDto>(url);
            return data;
        },
        enabled: !!id,
    });

    const {
        mutateAsync: createNewAndOffer, 
        isPending: isCreatingNewAndOffer, 
        isError: isErrorCreatingNewAndOffer
    } = useMutation({
        mutationFn: async ({ dto, imageData }: { dto: CreateNewAndOfferRequestDto; imageData?: File | string }) => {
            const response = await axiosInstance.post(
                ApiConfig.queries.features.newsAndOffers.endpoints.create, dto
            );  

            if(imageData instanceof File && response.data.id && response.status === 200) {
                const formData = new FormData();
                formData.append('id', response.data.id);
                formData.append('fileName', `${response.data.id}_${Date.now()}`);
                formData.append('file', imageData); 

                try {
                    const imageResponse = await axiosInstance.post(
                        ApiConfig.queries.features.newsAndOffers.endpoints.createImage, formData, {
                            headers: {
                                "Content-Type": "multipart/form-data"
                            }
                        }
                    );  
                    return { data: imageResponse.data, imageUploaded: true };
                } catch {
                    EToast({
                        variant: 'error', 
                        message: t('main.newsAndOffers.toasts.createPost.errorImage.message'), 
                        description: t('main.newsAndOffers.toasts.createPost.errorImage.description')
                    });
                    return { data: response.data, imageUploaded: false }; 
                }
            }

            return { ...response.data, imageUploaded: false };            
        },
        onSuccess: async () => {
            await queryClient.invalidateQueries({
                    queryKey: [ApiConfig.queries.features.newsAndOffers.queryKeys.list]
                });
            EToast({
                variant: 'success', 
                message: t('main.newsAndOffers.toasts.createPost.success.message'), 
                description: t('main.newsAndOffers.toasts.createPost.success.description')
            });
        },
        onError: () => {
            EToast({
                variant: 'error', 
                message: t('main.newsAndOffers.toasts.createPost.error.message'), 
                description: t('main.newsAndOffers.toasts.createPost.error.description')
            });
        }
    });

    const {
        mutateAsync: updateNewAndOffer, 
        isPending: isUpdatingingNewAndOffer, 
        isError: isErrorUpdatingNewAndOffer
    } = useMutation({
        mutationFn: async ({ dto, imageData }: { dto: CreateNewAndOfferRequestDto; imageData?: File | string }) => {
            const response = await axiosInstance.put(
                `${ApiConfig.queries.features.newsAndOffers.endpoints.update}/${id}`, dto
            );           

            if(imageData instanceof File && response.status === 200) {
                const formData = new FormData();
                formData.append('id', `${id}`);
                formData.append('fileName', `${id}_${Date.now()}`);
                formData.append('file', imageData); 

                try {
                    const imageResponse = await axiosInstance.post(
                        ApiConfig.queries.features.newsAndOffers.endpoints.createImage, formData, {
                            headers: {
                                "Content-Type": "multipart/form-data"
                            }
                        }
                    );  
                    return { data: imageResponse.data, imageUploaded: true };
                } catch {
                    EToast({
                        variant: 'error', 
                        message: t('main.newsAndOffers.toasts.updatePost.errorImage.message'), 
                        description: t('main.newsAndOffers.toasts.updatePost.errorImage.description')
                    });
                    return { data: response.data, imageUploaded: false }; 
                }
            }

            return { ...response.data, imageUploaded: false };            
        },
        onSuccess: async () => {
            await queryClient.invalidateQueries({
                    queryKey: [ApiConfig.queries.features.newsAndOffers.queryKeys.list]
                });
            EToast({
                variant: 'success', 
                message: t('main.newsAndOffers.toasts.updatePost.success.message'), 
                description: t('main.newsAndOffers.toasts.updatePost.success.description')
            });
        },
        onError: () => {
            EToast({
                variant: 'error', 
                message: t('main.newsAndOffers.toasts.updatePost.error.message'), 
                description: t('main.newsAndOffers.toasts.updatePost.error.description')
            });
        }
    });

    const {
        mutateAsync: deleteNewAndOffer, 
        isPending: isDeletingNewAndOffer, 
        isError: isErrorDeletingNewAndOffer
    } = useMutation({
        mutationFn: async (id: string) => {
            const response = await axiosInstance.delete(
                `${ApiConfig.queries.features.newsAndOffers.endpoints.delete}/${id}`
            );       

            return response.data;            
        },
        onSuccess: async () => {
            await queryClient.invalidateQueries({
                    queryKey: [ApiConfig.queries.features.newsAndOffers.queryKeys.list]
                });
            EToast({
                variant: 'success', 
                message: t('main.newsAndOffers.toasts.deletePost.success.message'), 
                description: t('main.newsAndOffers.toasts.deletePost.success.description')
            });
        },
        onError: () => {
            EToast({
                variant: 'error', 
                message: t('main.newsAndOffers.toasts.deletePost.error.message'), 
                description: t('main.newsAndOffers.toasts.deletePost.error.description')
            });
        }
    });

    const {
        mutateAsync: changeNewAndOfferStatus, 
        isPending: isChangingNewAndOfferStatus, 
        isError: isErrorChangingNewAndOfferStatus
    } = useMutation({
        mutationFn: async ({ id, status }: { id: string; status: NewsAndOffersStatus }) => {
            const response = await axiosInstance.patch(
                `${ApiConfig.queries.features.newsAndOffers.endpoints.changeStatus}/${id}/${status}`
            );       

            return response.data;            
        },
        onSuccess: async () => {
            await queryClient.invalidateQueries({
                    queryKey: [ApiConfig.queries.features.newsAndOffers.queryKeys.list]
                });
        },
        onError: () => {},
    });

    return {
        newAndOfferDetailsData, 
        isLoadingNewAndOfferDetailsData, 
        isErrorNewAndOfferDetailsData,
        createNewAndOffer, 
        isCreatingNewAndOffer, 
        isErrorCreatingNewAndOffer,
        updateNewAndOffer, 
        isUpdatingingNewAndOffer, 
        isErrorUpdatingNewAndOffer,
        deleteNewAndOffer, 
        isDeletingNewAndOffer, 
        isErrorDeletingNewAndOffer,
        changeNewAndOfferStatus, 
        isChangingNewAndOfferStatus, 
        isErrorChangingNewAndOfferStatus
    }
};
